import {
    getTimeSpanParts,
    swedishTimeZoneDate,
    TIME_SPAN_UNITS
} from 'tradera/utils/date';
import {
    DAY_IN_SECONDS,
    HOUR_IN_SECONDS,
    MINUTE_IN_SECONDS
} from 'tradera/constants/time';
import type { Dayjs } from 'dayjs';
import type { TranslationFunction } from 'tradera/lang/translate';

const hoursOrEmpty = (hours: number, t: TranslationFunction): string =>
    hours ? t('vip_hours', { count: hours }) : '';

const minutesOrEmpty = (minutes: number, t: TranslationFunction): string =>
    minutes ? t('vip_minutes', { count: minutes }) : '';

export const formatVipTimeLeft = (
    secondsLeft: number,
    t: TranslationFunction
): string => {
    if (secondsLeft < 1) {
        return '';
    }
    let formattedTimeLeft: string;
    if (secondsLeft > DAY_IN_SECONDS) {
        const { days, hours } = getTimeSpanParts(secondsLeft, {
            endUnit: TIME_SPAN_UNITS.HOURS
        });
        formattedTimeLeft = (
            t('vip_days', { count: days }) +
            ' ' +
            hoursOrEmpty(hours, t)
        ).trim();
    } else if (secondsLeft > HOUR_IN_SECONDS) {
        const { hours, minutes } = getTimeSpanParts(secondsLeft, {
            startUnit: TIME_SPAN_UNITS.HOURS,
            endUnit: TIME_SPAN_UNITS.MINUTES
        });
        formattedTimeLeft = (
            t('vip_hours', { count: hours }) +
            ' ' +
            minutesOrEmpty(minutes, t)
        ).trim();
    } else if (secondsLeft > MINUTE_IN_SECONDS) {
        const { minutes, seconds } = getTimeSpanParts(secondsLeft, {
            startUnit: TIME_SPAN_UNITS.MINUTES,
            endUnit: TIME_SPAN_UNITS.SECONDS
        });
        formattedTimeLeft =
            t('vip_minutes', { count: minutes }) +
            ' ' +
            t('vip_seconds', { count: seconds || 0 });
    } else {
        formattedTimeLeft = t('vip_seconds', { count: secondsLeft });
    }
    return formattedTimeLeft;
};

export const formatItemCardTimeLeft = (
    secondsLeft: number,
    t: TranslationFunction,
    endDateInput: string | Date,
    nowDateInput: string | Date,
    dayjs: () => Dayjs
): string => {
    if (Number.isInteger(secondsLeft)) {
        const endDate = swedishTimeZoneDate(endDateInput, dayjs);
        const now = swedishTimeZoneDate(nowDateInput, dayjs);

        if (secondsLeft > DAY_IN_SECONDS) {
            if (endDate.isSameOrAfter(now.add(2, 'day'), 'day')) {
                return endDate.format('D MMM HH:mm');
            }
            if (endDate.isSameOrAfter(now.add(1, 'day'))) {
                return `${t('vip_tomorrow')} ${endDate.format('HH:mm')}`;
            }
        }
        if (secondsLeft > HOUR_IN_SECONDS) {
            const ifValueNotZeroShowLabel = (
                label: string,
                value: number
            ): string => (value > 0 ? ' ' + t(label, { count: value }) : '');
            const hours = Math.floor(secondsLeft / HOUR_IN_SECONDS);
            const minutes = Math.floor(
                Math.floor(secondsLeft % HOUR_IN_SECONDS) / MINUTE_IN_SECONDS
            );
            return (
                t('vip_hours', { count: hours }) +
                ifValueNotZeroShowLabel('vip_minutes', minutes)
            );
        }

        if (secondsLeft > 2 * MINUTE_IN_SECONDS) {
            const minutes = Math.floor(secondsLeft / MINUTE_IN_SECONDS);
            return t('vip_minutes', { count: minutes });
        }

        return t('vip_seconds', { count: secondsLeft });
    } else {
        return '';
    }
};
